import React, { useState, useRef, useEffect, useMemo } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import '../poster_wizard.css';

const GOOGLE_MAPS_API_KEY = 'AIzaSyA1fQ3PPqStI2hluImIPswrqIFYcSobIZI';

const containerStyle = {
    width: '100%',
    height: '100%',
    minHeight: '400px'
};

const defaultCenter = {
    lat: 39.8283, // Geographic center of USA
    lng: -98.5795
};

export default function MapLocationDialog({ isOpen, onClose, onSaveLocation, initialLocation }) {
    const [viewMode, setViewMode] = useState('map'); // 'map' or 'fields'
    const [searchQuery, setSearchQuery] = useState('');
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [mapZoom, setMapZoom] = useState(4); // Default zoom to show whole USA
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [shouldLoadMap, setShouldLoadMap] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [showStateDropdown, setShowStateDropdown] = useState(false);
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const mapRef = useRef(null);
    const geocoderRef = useRef(null);
    const countryDropdownRef = useRef(null);
    const stateDropdownRef = useRef(null);
    const cityDropdownRef = useRef(null);

    // Reset map state when dialog opens/closes
    useEffect(() => {
        if (!isOpen) {
            setMapLoaded(false);
            setSelectedLocation(null);
            setSearchQuery('');
            setViewMode('map');
            setShouldLoadMap(false);
            setMapZoom(4); // Reset to show whole USA
            mapRef.current = null;
            geocoderRef.current = null;
        } else {
            // Reset map center when dialog opens
            setMapCenter(defaultCenter);
            setMapZoom(4); // Set zoom to show whole USA
            // Delay map loading to ensure dialog is fully rendered and visible
            const timer = setTimeout(() => {
                setShouldLoadMap(true);
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Initialize with initial location if provided
    useEffect(() => {
        if (initialLocation && isOpen && viewMode === 'map') {
            geocodeAddress(initialLocation);
        }
    }, [initialLocation, isOpen, viewMode]);

    // Reset state and city when country changes
    useEffect(() => {
        if (selectedCountry) {
            setSelectedState('');
            setSelectedCity('');
            // Clear map selection when fields are selected
            setSelectedLocation(null);
        }
    }, [selectedCountry]);

    // Reset city when state changes
    useEffect(() => {
        if (selectedState) {
            setSelectedCity('');
            // Clear map selection when fields are selected
            setSelectedLocation(null);
        }
    }, [selectedState]);

    // Clear map selection when city is selected
    useEffect(() => {
        if (selectedCity) {
            // Clear map selection when fields are selected
            setSelectedLocation(null);
        }
    }, [selectedCity]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setShowCountryDropdown(false);
            }
            if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
                setShowStateDropdown(false);
            }
            if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
                setShowCityDropdown(false);
            }
        };

        if (showCountryDropdown || showStateDropdown || showCityDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCountryDropdown, showStateDropdown, showCityDropdown]);

    // Geocode address to coordinates
    const geocodeAddress = async (address) => {
        if (!address || address.trim() === '') return;

        try {
            const geocodeURL = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`;
            const response = await fetch(geocodeURL);
            const data = await response.json();

            if (data.status === 'OK' && data.results && data.results.length > 0) {
                const location = data.results[0].geometry.location;
                const newCenter = {
                    lat: location.lat,
                    lng: location.lng
                };
                setMapCenter(newCenter);

                // Determine appropriate zoom level based on location type
                let zoomLevel = 12; // Default for cities/addresses
                const addressLower = address.toLowerCase();
                if (addressLower === 'usa' || addressLower === 'united states' || addressLower === 'united states of america') {
                    zoomLevel = 4; // Show whole USA
                } else if (data.results[0].types.includes('country')) {
                    zoomLevel = 5; // Show whole country
                } else if (data.results[0].types.includes('administrative_area_level_1')) {
                    zoomLevel = 6; // Show state
                } else if (data.results[0].types.includes('locality')) {
                    zoomLevel = 10; // Show city
                }

                setMapZoom(zoomLevel);
                setSelectedLocation({
                    ...newCenter,
                    address: data.results[0].formatted_address
                });

                if (mapRef.current) {
                    mapRef.current.setCenter(newCenter);
                    mapRef.current.setZoom(zoomLevel);
                }
            }
        } catch (error) {
            console.error('Error geocoding address:', error);
        }
    };

    // Handle map click
    const handleMapClick = async (event) => {
        if (!mapLoaded) return;

        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const newLocation = { lat, lng };

        setMapCenter(newLocation);
        setSelectedLocation(newLocation);

        // Reverse geocode to get address
        try {
            if (!geocoderRef.current && window.google && window.google.maps) {
                geocoderRef.current = new window.google.maps.Geocoder();
            }

            if (geocoderRef.current) {
                geocoderRef.current.geocode(
                    { location: newLocation },
                    (results, status) => {
                        if (status === 'OK' && results && results.length > 0) {
                            setSelectedLocation({
                                ...newLocation,
                                address: results[0].formatted_address
                            });
                        }
                    }
                );
            }
        } catch (error) {
            console.error('Error reverse geocoding:', error);
        }
    };

    // Handle search
    const handleSearch = async (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            await geocodeAddress(searchQuery);
        }
    };

    // Handle save
    const handleSave = () => {
        if (onSaveLocation) {
            // If fields are selected (and we're in fields view or fields have been modified), prioritize fields
            if (selectedCountry || selectedState || selectedCity) {
                const addressParts = [];
                if (selectedCity) addressParts.push(selectedCity);
                if (selectedState) addressParts.push(selectedState);
                if (selectedCountry) addressParts.push(selectedCountry);

                const address = addressParts.join(', ');

                // Create a location object with the constructed address
                const locationFromFields = {
                    address: address,
                    lat: null,
                    lng: null
                };

                onSaveLocation(locationFromFields);
            }
            // If location was selected from map and no fields are selected, use it
            else if (selectedLocation && selectedLocation.address) {
                onSaveLocation(selectedLocation);
            }
        }
        onClose();
    };

    // Handle view map from fields
    const handleViewMap = () => {
        setViewMode('map');
    };

    // Country, State, and City data
    const countries = ['USA', 'China', 'India'];

    // States by country
    const statesByCountry = {
        'USA': [
            'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
            'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
            'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
            'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
            'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
            'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
            'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
        ],
        'China': [
            'Anhui', 'Beijing', 'Chongqing', 'Fujian', 'Gansu', 'Guangdong', 'Guangxi', 'Guizhou',
            'Hainan', 'Hebei', 'Heilongjiang', 'Henan', 'Hong Kong', 'Hubei', 'Hunan', 'Inner Mongolia',
            'Jiangsu', 'Jiangxi', 'Jilin', 'Liaoning', 'Macau', 'Ningxia', 'Qinghai', 'Shaanxi',
            'Shandong', 'Shanghai', 'Shanxi', 'Sichuan', 'Tianjin', 'Tibet', 'Xinjiang', 'Yunnan', 'Zhejiang'
        ],
        'India': [
            'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
            'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
            'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
            'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
            'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh',
            'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh',
            'Lakshadweep', 'Puducherry'
        ]
    };

    // Cities by state (USA)
    const citiesByStateUSA = {
        'Alabama': ['Birmingham', 'Montgomery', 'Mobile', 'Huntsville', 'Tuscaloosa', 'Hoover', 'Auburn', 'Dothan'],
        'Alaska': ['Anchorage', 'Fairbanks', 'Juneau', 'Sitka', 'Ketchikan', 'Wasilla', 'Kenai', 'Kodiak'],
        'Arizona': ['Phoenix', 'Tucson', 'Mesa', 'Chandler', 'Scottsdale', 'Glendale', 'Gilbert', 'Tempe'],
        'Arkansas': ['Little Rock', 'Fayetteville', 'Fort Smith', 'Jonesboro', 'North Little Rock', 'Conway', 'Rogers', 'Pine Bluff'],
        'California': ['Los Angeles', 'San Diego', 'San Jose', 'San Francisco', 'Fresno', 'Sacramento', 'Long Beach', 'Oakland'],
        'Colorado': ['Denver', 'Colorado Springs', 'Aurora', 'Fort Collins', 'Lakewood', 'Thornton', 'Arvada', 'Westminster'],
        'Connecticut': ['Bridgeport', 'New Haven', 'Hartford', 'Stamford', 'Waterbury', 'Norwalk', 'Danbury', 'New Britain'],
        'Delaware': ['Wilmington', 'Dover', 'Newark', 'Middletown', 'Smyrna', 'Milford', 'Seaford', 'Georgetown'],
        'Florida': ['Jacksonville', 'Miami', 'Tampa', 'Orlando', 'St. Petersburg', 'Hialeah', 'Tallahassee', 'Fort Lauderdale'],
        'Georgia': ['Atlanta', 'Augusta', 'Columbus', 'Savannah', 'Athens', 'Sandy Springs', 'Roswell', 'Macon'],
        'Hawaii': ['Honolulu', 'Hilo', 'Kailua', 'Kaneohe', 'Pearl City', 'Waipahu', 'Kahului', 'Kihei'],
        'Idaho': ['Boise', 'Nampa', 'Meridian', 'Idaho Falls', 'Pocatello', 'Caldwell', 'Coeur d\'Alene', 'Twin Falls'],
        'Illinois': ['Chicago', 'Aurora', 'Rockford', 'Joliet', 'Naperville', 'Springfield', 'Peoria', 'Elgin'],
        'Indiana': ['Indianapolis', 'Fort Wayne', 'Evansville', 'South Bend', 'Carmel', 'Fishers', 'Bloomington', 'Hammond'],
        'Iowa': ['Des Moines', 'Cedar Rapids', 'Davenport', 'Sioux City', 'Iowa City', 'Waterloo', 'Council Bluffs', 'Ames'],
        'Kansas': ['Wichita', 'Overland Park', 'Kansas City', 'Olathe', 'Topeka', 'Lawrence', 'Shawnee', 'Manhattan'],
        'Kentucky': ['Louisville', 'Lexington', 'Bowling Green', 'Owensboro', 'Covington', 'Hopkinsville', 'Richmond', 'Florence'],
        'Louisiana': ['New Orleans', 'Baton Rouge', 'Shreveport', 'Lafayette', 'Lake Charles', 'Kenner', 'Bossier City', 'Monroe'],
        'Maine': ['Portland', 'Lewiston', 'Bangor', 'South Portland', 'Auburn', 'Biddeford', 'Sanford', 'Saco'],
        'Maryland': ['Baltimore', 'Frederick', 'Rockville', 'Gaithersburg', 'Bowie', 'Annapolis', 'College Park', 'Salisbury'],
        'Massachusetts': ['Boston', 'Worcester', 'Springfield', 'Lowell', 'Cambridge', 'New Bedford', 'Brockton', 'Quincy'],
        'Michigan': ['Detroit', 'Grand Rapids', 'Warren', 'Sterling Heights', 'Lansing', 'Ann Arbor', 'Flint', 'Dearborn'],
        'Minnesota': ['Minneapolis', 'St. Paul', 'Rochester', 'Duluth', 'Bloomington', 'Brooklyn Park', 'Plymouth', 'St. Cloud'],
        'Mississippi': ['Jackson', 'Gulfport', 'Southaven', 'Hattiesburg', 'Biloxi', 'Meridian', 'Tupelo', 'Greenville'],
        'Missouri': ['Kansas City', 'St. Louis', 'Springfield', 'Columbia', 'Independence', 'Lee\'s Summit', 'O\'Fallon', 'St. Joseph'],
        'Montana': ['Billings', 'Missoula', 'Great Falls', 'Bozeman', 'Butte', 'Helena', 'Kalispell', 'Havre'],
        'Nebraska': ['Omaha', 'Lincoln', 'Bellevue', 'Grand Island', 'Kearney', 'Fremont', 'Hastings', 'North Platte'],
        'Nevada': ['Las Vegas', 'Henderson', 'Reno', 'North Las Vegas', 'Sparks', 'Carson City', 'Fernley', 'Elko'],
        'New Hampshire': ['Manchester', 'Nashua', 'Concord', 'Derry', 'Rochester', 'Dover', 'Salem', 'Merrimack'],
        'New Jersey': ['Newark', 'Jersey City', 'Paterson', 'Elizabeth', 'Edison', 'Woodbridge', 'Lakewood', 'Toms River'],
        'New Mexico': ['Albuquerque', 'Las Cruces', 'Rio Rancho', 'Santa Fe', 'Roswell', 'Farmington', 'Clovis', 'Hobbs'],
        'New York': ['New York City', 'Buffalo', 'Rochester', 'Yonkers', 'Syracuse', 'Albany', 'New Rochelle', 'Mount Vernon'],
        'North Carolina': ['Charlotte', 'Raleigh', 'Greensboro', 'Durham', 'Winston-Salem', 'Fayetteville', 'Cary', 'Wilmington'],
        'North Dakota': ['Fargo', 'Bismarck', 'Grand Forks', 'Minot', 'West Fargo', 'Williston', 'Dickinson', 'Mandan'],
        'Ohio': ['Columbus', 'Cleveland', 'Cincinnati', 'Toledo', 'Akron', 'Dayton', 'Parma', 'Canton'],
        'Oklahoma': ['Oklahoma City', 'Tulsa', 'Norman', 'Broken Arrow', 'Lawton', 'Edmond', 'Moore', 'Midwest City'],
        'Oregon': ['Portland', 'Eugene', 'Salem', 'Gresham', 'Hillsboro', 'Bend', 'Beaverton', 'Medford'],
        'Pennsylvania': ['Philadelphia', 'Pittsburgh', 'Allentown', 'Erie', 'Reading', 'Scranton', 'Bethlehem', 'Lancaster'],
        'Rhode Island': ['Providence', 'Warwick', 'Cranston', 'Pawtucket', 'East Providence', 'Woonsocket', 'Newport', 'Central Falls'],
        'South Carolina': ['Charleston', 'Columbia', 'North Charleston', 'Mount Pleasant', 'Rock Hill', 'Greenville', 'Summerville', 'Sumter'],
        'South Dakota': ['Sioux Falls', 'Rapid City', 'Aberdeen', 'Brookings', 'Watertown', 'Mitchell', 'Yankton', 'Pierre'],
        'Tennessee': ['Nashville', 'Memphis', 'Knoxville', 'Chattanooga', 'Clarksville', 'Murfreesboro', 'Franklin', 'Jackson'],
        'Texas': ['Houston', 'San Antonio', 'Dallas', 'Austin', 'Fort Worth', 'El Paso', 'Arlington', 'Corpus Christi'],
        'Utah': ['Salt Lake City', 'West Valley City', 'Provo', 'West Jordan', 'Orem', 'Sandy', 'Ogden', 'St. George'],
        'Vermont': ['Burlington', 'Essex', 'South Burlington', 'Colchester', 'Rutland', 'Montpelier', 'Barre', 'St. Albans'],
        'Virginia': ['Virginia Beach', 'Norfolk', 'Chesapeake', 'Richmond', 'Newport News', 'Alexandria', 'Hampton', 'Portsmouth'],
        'Washington': ['Seattle', 'Spokane', 'Tacoma', 'Vancouver', 'Bellevue', 'Everett', 'Kent', 'Yakima'],
        'West Virginia': ['Charleston', 'Huntington', 'Parkersburg', 'Morgantown', 'Wheeling', 'Martinsburg', 'Fairmont', 'Beckley'],
        'Wisconsin': ['Milwaukee', 'Madison', 'Green Bay', 'Kenosha', 'Racine', 'Appleton', 'Waukesha', 'Oshkosh'],
        'Wyoming': ['Cheyenne', 'Casper', 'Laramie', 'Gillette', 'Rock Springs', 'Sheridan', 'Green River', 'Evanston']
    };

    // Cities by state (China)
    const citiesByStateChina = {
        'Beijing': ['Beijing'],
        'Shanghai': ['Shanghai'],
        'Guangdong': ['Guangzhou', 'Shenzhen', 'Dongguan', 'Foshan', 'Zhongshan', 'Huizhou', 'Zhuhai', 'Jiangmen'],
        'Jiangsu': ['Nanjing', 'Suzhou', 'Wuxi', 'Changzhou', 'Xuzhou', 'Nantong', 'Yangzhou', 'Zhenjiang'],
        'Zhejiang': ['Hangzhou', 'Ningbo', 'Wenzhou', 'Jiaxing', 'Huzhou', 'Shaoxing', 'Jinhua', 'Taizhou'],
        'Shandong': ['Jinan', 'Qingdao', 'Yantai', 'Weifang', 'Jining', 'Linyi', 'Zibo', 'Dezhou'],
        'Sichuan': ['Chengdu', 'Mianyang', 'Deyang', 'Nanchong', 'Yibin', 'Zigong', 'Luzhou', 'Leshan'],
        'Hubei': ['Wuhan', 'Xiangyang', 'Yichang', 'Jingzhou', 'Shiyan', 'Huanggang', 'Ezhou', 'Jingmen'],
        'Henan': ['Zhengzhou', 'Luoyang', 'Xinxiang', 'Nanyang', 'Kaifeng', 'Anyang', 'Pingdingshan', 'Xuchang'],
        'Hunan': ['Changsha', 'Zhuzhou', 'Xiangtan', 'Hengyang', 'Yueyang', 'Changde', 'Shaoyang', 'Yiyang'],
        'Fujian': ['Fuzhou', 'Xiamen', 'Quanzhou', 'Putian', 'Zhangzhou', 'Nanping', 'Longyan', 'Sanming'],
        'Anhui': ['Hefei', 'Wuhu', 'Bengbu', 'Huainan', 'Ma\'anshan', 'Anqing', 'Suzhou', 'Fuyang'],
        'Liaoning': ['Shenyang', 'Dalian', 'Anshan', 'Fushun', 'Benxi', 'Dandong', 'Jinzhou', 'Yingkou'],
        'Heilongjiang': ['Harbin', 'Qiqihar', 'Daqing', 'Mudanjiang', 'Jiamusi', 'Jixi', 'Hegang', 'Shuangyashan'],
        'Jilin': ['Changchun', 'Jilin', 'Siping', 'Liaoyuan', 'Tonghua', 'Baishan', 'Songyuan', 'Baicheng'],
        'Hebei': ['Shijiazhuang', 'Tangshan', 'Baoding', 'Handan', 'Qinhuangdao', 'Zhangjiakou', 'Chengde', 'Cangzhou'],
        'Shanxi': ['Taiyuan', 'Datong', 'Yangquan', 'Changzhi', 'Jincheng', 'Shuozhou', 'Jinzhong', 'Yuncheng'],
        'Shaanxi': ['Xi\'an', 'Baoji', 'Xianyang', 'Weinan', 'Yan\'an', 'Hanzhong', 'Yulin', 'Ankang'],
        'Inner Mongolia': ['Hohhot', 'Baotou', 'Chifeng', 'Ordos', 'Hulunbuir', 'Tongliao', 'Ulanqab', 'Bayannur'],
        'Xinjiang': ['Urumqi', 'Karamay', 'Turpan', 'Hami', 'Aksu', 'Kashgar', 'Ili', 'Hotan'],
        'Tibet': ['Lhasa', 'Shigatse', 'Chamdo', 'Nyingchi', 'Shannan', 'Nagqu', 'Ngari'],
        'Qinghai': ['Xining', 'Haidong', 'Haibei', 'Hainan', 'Huangnan', 'Golog', 'Yushu', 'Haixi'],
        'Gansu': ['Lanzhou', 'Tianshui', 'Baiyin', 'Jiayuguan', 'Jinchang', 'Wuwei', 'Zhangye', 'Pingliang'],
        'Ningxia': ['Yinchuan', 'Shizuishan', 'Wuzhong', 'Guyuan', 'Zhongwei'],
        'Yunnan': ['Kunming', 'Qujing', 'Yuxi', 'Baoshan', 'Zhaotong', 'Lijiang', 'Pu\'er', 'Lincang'],
        'Guizhou': ['Guiyang', 'Zunyi', 'Liupanshui', 'Anshun', 'Bijie', 'Tongren', 'Qiandongnan', 'Qiannan'],
        'Guangxi': ['Nanning', 'Liuzhou', 'Guilin', 'Wuzhou', 'Beihai', 'Fangchenggang', 'Qinzhou', 'Guigang'],
        'Hainan': ['Haikou', 'Sanya', 'Danzhou', 'Wenchang', 'Qionghai', 'Wanning', 'Dongfang', 'Lingao'],
        'Jiangxi': ['Nanchang', 'Ganzhou', 'Jiujiang', 'Shangrao', 'Fuzhou', 'Yichun', 'Jingdezhen', 'Pingxiang'],
        'Chongqing': ['Chongqing'],
        'Tianjin': ['Tianjin'],
        'Hong Kong': ['Hong Kong'],
        'Macau': ['Macau']
    };

    // Cities by state (India)
    const citiesByStateIndia = {
        'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Rajahmundry', 'Tirupati', 'Kakinada'],
        'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Pasighat', 'Tawang', 'Ziro', 'Bomdila', 'Tezu', 'Along'],
        'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon', 'Tinsukia', 'Tezpur', 'Bongaigaon'],
        'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga', 'Arrah', 'Katihar'],
        'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Durg', 'Rajnandgaon', 'Raigarh', 'Jagdalpur'],
        'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Ponda', 'Mapusa', 'Bicholim', 'Curchorem', 'Quepem'],
        'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Anand'],
        'Haryana': ['Faridabad', 'Gurgaon', 'Panipat', 'Ambala', 'Yamunanagar', 'Rohtak', 'Hisar', 'Karnal'],
        'Himachal Pradesh': ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Bilaspur', 'Kullu', 'Chamba', 'Kangra'],
        'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh', 'Giridih', 'Deoghar', 'Phusro'],
        'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belgaum', 'Gulbarga', 'Davangere', 'Bellary'],
        'Kerala': ['Kochi', 'Thiruvananthapuram', 'Kozhikode', 'Thrissur', 'Kannur', 'Kollam', 'Alappuzha', 'Palakkad'],
        'Madhya Pradesh': ['Indore', 'Bhopal', 'Gwalior', 'Jabalpur', 'Raipur', 'Ujjain', 'Sagar', 'Dewas'],
        'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Amravati'],
        'Manipur': ['Imphal', 'Thoubal', 'Bishnupur', 'Churachandpur', 'Ukhrul', 'Kakching', 'Lilong', 'Mayang Imphal'],
        'Meghalaya': ['Shillong', 'Tura', 'Jowai', 'Nongpoh', 'Nongstoin', 'Baghmara', 'Williamnagar', 'Resubelpara'],
        'Mizoram': ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib', 'Serchhip', 'Lawngtlai', 'Mamit'],
        'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto', 'Phek', 'Mon'],
        'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Brahmapur', 'Sambalpur', 'Puri', 'Balasore', 'Bhadrak'],
        'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Pathankot', 'Hoshiarpur', 'Moga'],
        'Rajasthan': ['Jaipur', 'Jodhpur', 'Kota', 'Bikaner', 'Ajmer', 'Udaipur', 'Bhilwara', 'Alwar'],
        'Sikkim': ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Singtam', 'Rangpo', 'Jorethang', 'Ravangla'],
        'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Erode', 'Vellore'],
        'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Ramagundam', 'Khammam', 'Mahbubnagar', 'Nalgonda'],
        'Tripura': ['Agartala', 'Udaipur', 'Dharmanagar', 'Kailasahar', 'Belonia', 'Khowai', 'Teliamura', 'Ambassa'],
        'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Allahabad', 'Meerut', 'Ghaziabad', 'Noida'],
        'Uttarakhand': ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Rudrapur', 'Kashipur', 'Rishikesh', 'Nainital'],
        'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman', 'Malda', 'Kharagpur'],
        'Andaman and Nicobar Islands': ['Port Blair', 'Diglipur', 'Mayabunder', 'Rangat', 'Car Nicobar', 'Hut Bay', 'Long Island', 'Little Andaman'],
        'Chandigarh': ['Chandigarh'],
        'Dadra and Nagar Haveli and Daman and Diu': ['Daman', 'Silvassa', 'Diu'],
        'Delhi': ['New Delhi', 'Delhi'],
        'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Sopore', 'Kathua', 'Udhampur', 'Rajouri'],
        'Ladakh': ['Leh', 'Kargil'],
        'Lakshadweep': ['Kavaratti', 'Agatti', 'Amini', 'Andrott', 'Bitra', 'Chetlat', 'Kadmat', 'Kalpeni'],
        'Puducherry': ['Puducherry', 'Karaikal', 'Mahe', 'Yanam']
    };

    // Get states based on selected country
    const getStates = () => {
        if (!selectedCountry) return [];
        return statesByCountry[selectedCountry] || [];
    };

    // Get cities based on selected state
    const getCities = () => {
        if (!selectedState) return [];

        if (selectedCountry === 'USA') {
            return citiesByStateUSA[selectedState] || [];
        } else if (selectedCountry === 'China') {
            return citiesByStateChina[selectedState] || [];
        } else if (selectedCountry === 'India') {
            return citiesByStateIndia[selectedState] || [];
        }
        return [];
    };

    const states = getStates();
    const cities = getCities();

    // Create marker icon config safely
    const markerIcon = useMemo(() => {
        const baseIcon = {
            url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
        };

        // Only add scaledSize if Size constructor is available and map is loaded
        if (mapLoaded && window.google && window.google.maps && window.google.maps.Size && typeof window.google.maps.Size === 'function') {
            try {
                return {
                    ...baseIcon,
                    scaledSize: new window.google.maps.Size(40, 40)
                };
            } catch (e) {
                console.warn('Could not create Size for marker icon:', e);
                return baseIcon;
            }
        }

        return baseIcon;
    }, [mapLoaded]);

    if (!isOpen) return null;

    return (
        <>
            <div className="map-dialog-overlay" onClick={onClose}></div>
            <div className="map-dialog-container">
                <div className="map-dialog-header">
                    <h2 className="map-dialog-title">Tap on the Map to Select Location</h2>
                    <button className="map-dialog-close" onClick={onClose}>×</button>
                </div>

                {viewMode === 'map' ? (
                    <>
                        <div className="map-dialog-search">
                            <form onSubmit={handleSearch}>
                                <input
                                    type="text"
                                    className="map-dialog-search-input"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </form>
                        </div>

                        <div className="map-dialog-map-container" id="map-dialog-map-location">
                            {isOpen && viewMode === 'map' && shouldLoadMap && (
                                <LoadScript
                                    googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                                    loadingElement={
                                        <div className="map-dialog-loading">
                                            Loading map...
                                        </div>
                                    }
                                >
                                    <GoogleMap
                                        mapContainerStyle={containerStyle}
                                        center={mapCenter}
                                        zoom={mapZoom}
                                        onClick={handleMapClick}
                                        onLoad={(map) => {
                                            mapRef.current = map;
                                            setMapLoaded(true);
                                            if (window.google && window.google.maps) {
                                                geocoderRef.current = new window.google.maps.Geocoder();
                                                // Set initial zoom
                                                map.setZoom(mapZoom);
                                                // Trigger resize multiple times to ensure map displays correctly
                                                const resizeMap = () => {
                                                    if (window.google && window.google.maps && map) {
                                                        try {
                                                            window.google.maps.event.trigger(map, 'resize');
                                                            map.setCenter(mapCenter);
                                                            map.setZoom(mapZoom);
                                                        } catch (e) {
                                                            console.error('Error triggering map resize:', e);
                                                        }
                                                    }
                                                };
                                                setTimeout(resizeMap, 100);
                                                setTimeout(resizeMap, 500);
                                                setTimeout(resizeMap, 1000);
                                            }
                                        }}
                                        onZoomChanged={() => {
                                            if (mapRef.current) {
                                                setMapZoom(mapRef.current.getZoom());
                                            }
                                        }}
                                        options={{
                                            disableDefaultUI: false,
                                            zoomControl: true,
                                            zoomControlOptions: {
                                                position: 4 // RIGHT_CENTER
                                            },
                                            streetViewControl: false,
                                            mapTypeControl: false,
                                            fullscreenControl: false,
                                        }}
                                    >
                                        {selectedLocation && mapLoaded && window.google && window.google.maps && (
                                            <Marker
                                                position={selectedLocation}
                                                icon={markerIcon}
                                            />
                                        )}
                                    </GoogleMap>
                                </LoadScript>
                            )}
                            {isOpen && viewMode === 'map' && !shouldLoadMap && (
                                <div className="map-dialog-loading">
                                    Preparing map...
                                </div>
                            )}
                        </div>

                        <div className="map-dialog-footer">
                            <button className="map-dialog-button map-dialog-button-secondary" onClick={() => setViewMode('fields')}>
                                View Fields
                            </button>
                            <button className="map-dialog-button map-dialog-button-primary" onClick={handleSave}>
                                Save Location
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="map-dialog-fields">
                            <div className="map-dialog-field">
                                <label className="map-dialog-field-label">Country</label>
                                <div ref={countryDropdownRef} className={`map-dialog-dropdown ${showCountryDropdown ? 'open' : ''}`}>
                                    <div className="map-dialog-dropdown-trigger" onClick={() => setShowCountryDropdown(!showCountryDropdown)}>
                                        <span className="map-dialog-dropdown-text">{selectedCountry || 'Select'}</span>
                                        <ExpandMoreIcon className={`map-dialog-dropdown-icon ${showCountryDropdown ? 'rotated' : ''}`} />
                                    </div>
                                    {showCountryDropdown && (
                                        <div className="map-dialog-dropdown-menu">
                                            {countries.map((country) => (
                                                <div
                                                    key={country}
                                                    className="map-dialog-dropdown-item"
                                                    onClick={() => {
                                                        setSelectedCountry(country);
                                                        setShowCountryDropdown(false);
                                                    }}
                                                >
                                                    {country}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="map-dialog-field">
                                <label className="map-dialog-field-label">State</label>
                                <div ref={stateDropdownRef} className={`map-dialog-dropdown ${showStateDropdown ? 'open' : ''}`}>
                                    <div className="map-dialog-dropdown-trigger" onClick={() => setShowStateDropdown(!showStateDropdown)}>
                                        <span className="map-dialog-dropdown-text">{selectedState || 'Select'}</span>
                                        <ExpandMoreIcon className={`map-dialog-dropdown-icon ${showStateDropdown ? 'rotated' : ''}`} />
                                    </div>
                                    {showStateDropdown && (
                                        <div className="map-dialog-dropdown-menu">
                                            {states.map((state) => (
                                                <div
                                                    key={state}
                                                    className="map-dialog-dropdown-item"
                                                    onClick={() => {
                                                        setSelectedState(state);
                                                        setShowStateDropdown(false);
                                                    }}
                                                >
                                                    {state}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="map-dialog-field">
                                <label className="map-dialog-field-label">City</label>
                                <div ref={cityDropdownRef} className={`map-dialog-dropdown ${showCityDropdown ? 'open' : ''}`}>
                                    <div className="map-dialog-dropdown-trigger" onClick={() => setShowCityDropdown(!showCityDropdown)}>
                                        <span className="map-dialog-dropdown-text">{selectedCity || 'Select'}</span>
                                        <ExpandMoreIcon className={`map-dialog-dropdown-icon ${showCityDropdown ? 'rotated' : ''}`} />
                                    </div>
                                    {showCityDropdown && (
                                        <div className="map-dialog-dropdown-menu">
                                            {cities.map((city) => (
                                                <div
                                                    key={city}
                                                    className="map-dialog-dropdown-item"
                                                    onClick={() => {
                                                        setSelectedCity(city);
                                                        setShowCityDropdown(false);
                                                    }}
                                                >
                                                    {city}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="map-dialog-footer">
                            <button className="map-dialog-button map-dialog-button-secondary" onClick={handleViewMap}>
                                View Map
                            </button>
                            <button className="map-dialog-button map-dialog-button-primary" onClick={handleSave}>
                                Save Location
                            </button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

